import { Fragment, useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAtomValue } from 'jotai';
import '../../../recruit/common/Recruit.scss';
import './MinisterInfo.scss';
import MainURL from '../../../../MainURL';
import { userDataAtom } from '../../../../AtomStore';
import Loading from '../../../../components/Loading';
import { PORTFOLIO_CATEGORIES } from './ministerInfoTypes';
import type { MinisterInfoDetail as MinisterInfoDetailData, PortfolioItem } from './ministerInfoTypes';
import { SAMPLE_MINISTER_ID, getMockMinisterDetail } from './ministerInfoMock';
import { portfolioImageSrc, profileImageSrc } from './ministerInfoUtils';

// 목록의 이력서/자기소개서/사역 포트폴리오 버튼 → ?tab= 으로 해당 블록까지 스크롤
export const SECTION_IDS: Record<string, string> = {
  resume: 'minister-resume',
  intro: 'minister-intro',
  portfolio: 'minister-portfolio',
};

export function SelfIntroductionContent({ resume }: { resume: MinisterInfoDetailData }) {
  const items = resume.selfIntroduction || [];
  if (items.length === 0) {
    return <div className="minister-info__empty"><p>등록된 자기소개서가 없습니다.</p></div>;
  }
  return (
    <section>
      <div className="minister-info__intro">
        {items.map((item, index) => (
          <div key={`intro-${index}`} className="minister-info__intro-item">
            <h4>{index + 1}. {item.question}</h4>
            <p>{item.answer}</p>
          </div>
        ))}
      </div>
    </section>
  );
}

function PortfolioVideoCard({ item }: { item: PortfolioItem }) {
  const body = (
    <>
      <div className="minister-info__video-thumb">
        {item.image && <img src={portfolioImageSrc(item.image)} alt={item.title} />}
        <span className="minister-info__video-play" aria-hidden="true" />
      </div>
      <p className="minister-info__media-title">{item.title}</p>
      <p className="minister-info__media-meta">{[item.church, item.date].filter(Boolean).join(' · ')}</p>
      {item.description && <p className="minister-info__media-desc">{item.description}</p>}
    </>
  );
  return item.link ? (
    <a className="minister-info__media-card" href={item.link} target="_blank" rel="noreferrer">{body}</a>
  ) : (
    <div className="minister-info__media-card">{body}</div>
  );
}

export function PortfolioContent({ resume }: { resume: MinisterInfoDetailData }) {
  const items = resume.portfolioItems || [];
  return (
    <>
      {PORTFOLIO_CATEGORIES.map((category) => {
        const group = items.filter((item) => item.category === category);
        const isPhoto = category === '사역사진';
        return (
          <section key={category} className="minister-info__portfolio-group">
            <h1 className="section_title">
              {category} <span className="minister-info__group-count">{group.length}</span>
            </h1>
            {group.length === 0 ? (
              <p className="minister-info__group-empty">등록된 {category}이 없습니다.</p>
            ) : isPhoto ? (
              <div className="minister-info__photo-grid">
                {group.map((item, index) => (
                  <figure key={`photo-${index}`} className="minister-info__photo-item">
                    <img src={portfolioImageSrc(item.image)} alt={item.title} />
                    <figcaption>
                      <b>{item.title}</b>
                      <span>{[item.church, item.date].filter(Boolean).join(' · ')}</span>
                      {item.description && <span>{item.description}</span>}
                    </figcaption>
                  </figure>
                ))}
              </div>
            ) : (
              <div className="minister-info__video-grid">
                {group.map((item, index) => (
                  <PortfolioVideoCard key={`video-${index}`} item={item} />
                ))}
              </div>
            )}
          </section>
        );
      })}
    </>
  );
}

export function MinisterInfoDetailContent({ resume }: { resume: MinisterInfoDetailData }) {
  const birth = [resume.birthYear, resume.birthMonth, resume.birthDay].filter(Boolean).join('.');

  return (
    <>
      <section>
        <h1 className="section_title">기본 정보</h1>
        <div className="copInfoBx">
          <div className="copInfoBx-cover">
            <div className={`minister-info__photo${resume.id === SAMPLE_MINISTER_ID ? ' is-logo' : ''}`}>
              {resume.profileImage ? (
                <img src={profileImageSrc(resume.profileImage)} alt="증명사진" />
              ) : (
                <span>사진 없음</span>
              )}
            </div>
            <div className="copInfo">
              <div className="label">이름</div>
              <div className="value">{resume.name || '-'}</div>
              <div className="label">성별</div>
              <div className="value">{resume.gender || '-'}</div>
              <div className="label">생년월일</div>
              <div className="value">{birth || '-'}{resume.ageText ? ` (만 ${resume.ageText}세)` : ''}</div>
              <div className="label">휴대폰</div>
              <div className="value">{resume.phone ? <a href={`tel:${resume.phone}`}>{resume.phone}</a> : '-'}</div>
              <div className="label">이메일</div>
              <div className="value">{resume.email ? <a href={`mailto:${resume.email}`}>{resume.email}</a> : '-'}</div>
              <div className="label">주소</div>
              <div className="value">{[resume.address, resume.addressDetail].filter(Boolean).join(' ') || '-'}</div>
            </div>
          </div>
        </div>
      </section>

      {resume.educationItems.length > 0 && (
        <section>
          <h1 className="section_title">학력</h1>
          <div className="copInfoBx">
            <div className="copInfoBx-cover">
              <div className="copInfo">
                {resume.educationItems.map((item, index) => (
                  <Fragment key={`edu-${index}`}>
                    <div className="label">{item.schoolName || `학력 ${index + 1}`}</div>
                    <div className="value">
                      {item.major && `${item.major} · `}{item.startDate}~{item.endDate} {item.status && `(${item.status})`}
                    </div>
                  </Fragment>
                ))}
              </div>
            </div>
          </div>
        </section>
      )}

      {resume.careerItems.length > 0 && (
        <section>
          <h1 className="section_title">경력</h1>
          <div className="copInfoBx">
            <div className="copInfoBx-cover">
              <div className="copInfo">
                {resume.careerItems.map((item, index) => (
                  <Fragment key={`career-${index}`}>
                    <div className="label">{item.companyName || `경력 ${index + 1}`}</div>
                    <div className="value">
                      {item.employType && `[${item.employType}] `}{item.position && `${item.position} · `}{item.startDate}~{item.endDate}
                      {item.description && ` — ${item.description}`}
                    </div>
                  </Fragment>
                ))}
              </div>
            </div>
          </div>
        </section>
      )}

      <section>
        <h1 className="section_title">희망사항</h1>
        <div className="copInfoBx">
          <div className="copInfoBx-cover">
            <div className="copInfo">
              <div className="label">희망 유형</div>
              <div className="value">{resume.hopeType || '-'}</div>
              <div className="label">희망 지역</div>
              <div className="value">{resume.hopeLocation || '-'}</div>
              <div className="label">희망 급여</div>
              <div className="value">{resume.hopeSalaryPaySort === '면접 후 협의' ? '면접 후 협의' : resume.hopeSalary ? `${resume.hopeSalaryPaySort} ${resume.hopeSalary}만원` : '-'}</div>
              <div className="label">희망 역할</div>
              <div className="value">{resume.hopeRoles || '-'}</div>
              <div className="label">희망 부서</div>
              <div className="value">{resume.hopeDepartment || '-'}</div>
              <div className="label">희망 보험</div>
              <div className="value">{resume.hopeInsurance || '-'}</div>
              <div className="label">희망 복리후생</div>
              <div className="value">{resume.hopeWelfare || '-'}</div>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}

export default function MinisterInfoDetail() {
  const navigate = useNavigate();
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const resumeId = searchParams.get('id') || '';
  const tabParam = searchParams.get('tab') || '';
  const userData = useAtomValue(userDataAtom);
  const isSample = resumeId === SAMPLE_MINISTER_ID;

  // 샘플은 누구나 열람, 그 외는 로그인 회원만 — 교회 담당자가 아니면 서버가 치환 데이터(masked)를 내려주고 모자이크로 표시
  const [serverResume, setServerResume] = useState<MinisterInfoDetailData | null>(null);
  const [isLoading, setIsLoading] = useState(!isSample);
  const [errorMessage, setErrorMessage] = useState('');
  const resume: MinisterInfoDetailData | null = isSample ? getMockMinisterDetail(resumeId) : serverResume;

  const goList = () => navigate('/minister');

  useEffect(() => {
    if (isSample) return;
    if (!resumeId || !userData.userAccount) {
      setServerResume(null);
      setErrorMessage(resumeId ? '로그인 후 열람할 수 있습니다.' : '잘못된 접근입니다.');
      setIsLoading(false);
      return;
    }
    let cancelled = false;
    setIsLoading(true);
    setErrorMessage('');
    axios.get(`${MainURL}/resume/ministerdetail/${resumeId}`, {
      params: { userAccount: userData.userAccount },
    })
      .then((res) => {
        if (cancelled) return;
        if (res.data.success && res.data.resume) {
          setServerResume(res.data.resume);
        } else {
          setServerResume(null);
          setErrorMessage(res.data.error || '사역자정보를 불러오지 못했습니다.');
        }
      })
      .catch((err) => {
        console.error('사역자정보 상세 조회 실패:', err);
        if (cancelled) return;
        setServerResume(null);
        setErrorMessage(err.response?.data?.error || '사역자정보를 불러오지 못했습니다.');
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [isSample, resumeId, userData.userAccount]);

  useEffect(() => {
    if (!resume || !SECTION_IDS[tabParam] || tabParam === 'resume') return;
    document.getElementById(SECTION_IDS[tabParam])?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, [resume, tabParam]);

  return (
    <div className="recruit minister-info">
      <div className="inner">
        <div className="subpage__main">
          <>
            {isLoading ? (
              <div className="list-loading">
                <Loading />
              </div>
            ) : !resume ? (
              <div className="minister-info__guard">
                <h3>현재 열람할 수 없습니다</h3>
                <p>{errorMessage || '사역자정보를 불러오지 못했습니다.'}</p>
              </div>
            ) : (
              <>
                <section style={{ marginBottom: '30px' }}>
                  <div className="main_title_row">
                    <h3 className={`main_title${resume.masked ? ' minister-info__masked-text' : ''}`}>
                      {isSample && <span className="minister-info__sample-badge">샘플</span>}
                      {resume.title || `${resume.name} 님의 이력서`}
                    </h3>
                    <div className="list-btn" onClick={goList}>
                      <span>목록으로</span>
                    </div>
                  </div>
                  <p style={{ color: 'var(--ink-4)', fontSize: '16px' }}>수정일 {resume.updatedAt}</p>
                </section>

                {resume.masked && (
                  <div className="minister-info__masked-notice">
                    <h3>교회 담당자에게만 보여집니다</h3>
                    <p>이력서 내용은 교회 담당자 회원으로 로그인해야 열람할 수 있습니다.</p>
                  </div>
                )}

                <div className={resume.masked ? 'minister-info__masked' : undefined} aria-hidden={resume.masked || undefined}>
                <div id={SECTION_IDS.resume} className="minister-info__block">
                  <h2 className="minister-info__block-title">이력서</h2>
                  <MinisterInfoDetailContent resume={resume} />
                </div>

                <div id={SECTION_IDS.intro} className="minister-info__block">
                  <h2 className="minister-info__block-title">자기소개서</h2>
                  <SelfIntroductionContent resume={resume} />
                </div>

                <div id={SECTION_IDS.portfolio} className="minister-info__block">
                  <h2 className="minister-info__block-title">
                    사역 포트폴리오
                    {(resume.portfolioItems?.length ?? 0) > 0 && <span>{resume.portfolioItems?.length}</span>}
                  </h2>
                  <PortfolioContent resume={resume} />
                </div>
                </div>
              </>
            )}

            <div className="bottom-list-btn-wrap">
              <div className="bottom-list-btn-inner">
                <div className="list-btn list-btn--bottom" onClick={goList}>
                  <span>목록으로</span>
                </div>
              </div>
            </div>
          </>
        </div>
      </div>
    </div>
  );
}
